/**
 * The main emergency_data spatialLayer redux interaction is not a slice, and
 * can't be easily imported here in the datasource admin page. This small amount
 * of duplication of effort is significatly easier.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { getTokens } from '@rsos/sinatra';
import { checkStatus, parseJSON } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import { AdminState } from '../../../reducers';

export type SpatialLayer = {
  id: number;
  source: string;
  name: string;
  key: string;
  title: string;
  auto_display: boolean;
};

export type SpatialLayersState = {
  layers: SpatialLayer[];
  initialized: boolean;
  error?: null | string;
};

const initialState: SpatialLayersState = {
  layers: [],
  initialized: false,
  error: null,
};
const baseURL = `${getAPIHost()}`;

export const fetchSpatialLayers = createAsyncThunk<
  SpatialLayer[],
  void,
  { state: AdminState }
>(
  'appDataSource/fetchSpatialLayers',

  async (_, { getState }) => {
    const orgId = organizationSelector(getState())?.id;

    const tokens = getTokens();
    if (!tokens) {
      throw Error('Cannot retrieve layers');
    }
    const { token } = tokens;

    const response = await fetch(
      `${baseURL}/v1/spatial-layers/portal/${orgId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      },
    );
    await checkStatus(response);
    const data = await parseJSON(response);
    return (data?.layers || []) as SpatialLayer[];
  },
);

const spatialLayersSlice = createSlice({
  name: 'spatialLayers',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSpatialLayers.pending, state => {
        state.layers = [];
        state.initialized = false;
        state.error = null;
      })
      .addCase(fetchSpatialLayers.fulfilled, (state, { payload }) => {
        state.layers = payload;
        state.initialized = true;
        state.error = null;
      })
      .addCase(fetchSpatialLayers.rejected, (state, { error = {} }) => {
        state.layers = [];
        state.initialized = false;
        state.error = error.message;
      });
  },
});

export const spatialLayers = spatialLayersSlice.reducer;
